/**
 * Publish one of the user's saved templates to the shared gallery.
 *
 * The dialog only collects the title and description. Everything else is the
 * already-saved template, canonicalised and re-validated by `publicTemplatePayload`
 * before it goes over the wire.
 */

import { useState } from 'react';
import { Globe2, Loader2 } from 'lucide-react';
import { useEditor } from '../state/store';
import { Field, Modal } from '../ui/widgets/common';
import { UserTemplate, templatePreview } from '../templates/userTemplates';
import { publishPublicTemplate } from '../templates/publicTemplates';
import { ApiError } from './api';

export function PublishTemplateDialog({
  template,
  onClose,
  onPublished,
}: {
  template: UserTemplate;
  onClose: () => void;
  onPublished: () => void;
}): JSX.Element {
  const notify = useEditor((s) => s.notify);
  const [name, setName] = useState(template.name);
  const [description, setDescription] = useState(template.description);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const publish = async () => {
    if (busy) return;
    setError(null);
    if (!name.trim()) {
      setError('Give the template a name first.');
      return;
    }
    setBusy(true);
    try {
      await publishPublicTemplate(template, { name, description });
      notify(`Published "${name.trim()}" to public templates.`, 'success');
      onPublished();
      onClose();
    } catch (caught) {
      setError(
        caught instanceof ApiError || caught instanceof Error
          ? caught.message
          : 'Could not publish the template.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Publish template"
      description="Share a saved template with signed-in members"
      onClose={onClose}
      width={560}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn primary"
            onClick={() => void publish()}
            disabled={busy || !name.trim()}
          >
            {busy ? <Loader2 size={14} className="spin" /> : <Globe2 size={14} />}
            Publish
          </button>
        </>
      }
    >
      <div className="form-note">
        Public templates are visible to every signed-in member. Only the cells,
        variables, title and description leave this device; the server validates them
        as inert data and never executes template content.
      </div>

      <Field label="Name">
        <input
          className="input"
          value={name}
          maxLength={80}
          onChange={(event) => setName(event.target.value)}
          autoFocus
        />
      </Field>
      <label className="field" style={{ alignItems: 'start' }}>
        <span style={{ paddingTop: 7 }}>Description</span>
        <textarea
          className="input"
          rows={3}
          value={description}
          maxLength={300}
          placeholder="Optional — what is this template for?"
          onChange={(event) => setDescription(event.target.value)}
        />
      </label>

      <div className="faint" style={{ margin: '10px 0 6px' }}>
        Preview · {template.width}×{template.height}
      </div>
      <pre className="template-publish-preview">{templatePreview(template, 10)}</pre>

      {error ? <div className="form-note error">{error}</div> : null}
    </Modal>
  );
}
